
<template>
  <div class="base-list-page">
    <el-row class="detail-header-wrap">
      <BaseHeader
          :title="title"
          :class="titleClass"
          :icon="icon" />
      <slot name="headerAction" />
    </el-row>
    <BaseSearchConditionModal
        v-if="showSearchComponent"
        :no-condition="noCondition"
        :keyword="keyword"
        :is-and-search="isAndSearch"
        :conditions="conditions"
        :base-query="baseQuery"
        :store-path="storePath"
        :res-path="responsePath"
        :save-key="saveKey"
        @search="onSearch"
        @searched="onSearched" />
    <template
        v-else-if="showCustomSearchComponent">
      <slot name="customSearchComponent" />
    </template>
    <slot name="search" />
    <BaseTableList
        :class="tableClass"
        :table-set-list="tableSetList"
        :display-items="displayItems"
        :default-sort="sortCondition"
        @row-click="onRowClick"
        @sort-change="onSortChange">
      <template
          slot="operation"
          slot-scope="{ row }">
        <slot
            name="tableOperation"
            :row="row" />
      </template>
      <div slot="amount"
          slot-scope="{ row }"
          class="message-area">
        <div
            class="change-log-detail_button">
          <template v-if="amountReadonly(row.row)">
            <el-link type="primary"
                @click="onAmountButtonClick(row.row)">
              {{ rowAmountBtnLabel(row.row) }}
            </el-link>
          </template>
          <template v-else>
            <span>
              {{ rowAmountBtnLabel(row.row) }}
            </span>
          </template>
        </div>
      </div>
      <div slot="registered"
          slot-scope="{ row }"
          class="message-area">
        <div
            class="change-log-detail_button">
          <template v-if="registeredReadonly(row.row)">
            <el-link type="primary"
                @click="onRegisteredButtonClick(row.row)">
              {{ rowRegisteredBtnLabel(row.row) }}
            </el-link>
          </template>
          <template v-else>
            <span>
              {{ rowRegisteredBtnLabel(row.row) }}
            </span>
          </template>
        </div>
      </div>
    </BaseTableList>
    <StockMoveDialog
        :keys="stockMoveKeys"
        @saved="onRefreshRow" />
    <StockSetDialog
        :keys="stockSetKeys"
        @saved="onRefreshRow" />
    <BasePagination
        v-if="showPagination"
        :current-page="_currentPage"
        :size="pageSize"
        :total="totalSize"
        :total-count="totalCount"
        @size-change="onPaginationSizeChange"
        @current-change="onPaginationCurrentChange" />
  </div>
</template>
<style scoped>
.message-area {
  text-align: right;
  justify-content: space-between;
}
.change-log-message {
  font-family: sans-serif;
  white-space: pre-line;
  margin: 0;
  width: 100%;
}
.change-log-detail_button {
  padding-left: 10px;
}
.no-data-message {
  margin: 5px 15px;
}
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
  vertical-align: top;
}
.dialog-contents .detail-table ::v-deep th {
  min-width: 130px;
}
.dialog-contents .detail-table-list {
  border-top: 1px solid #c7d3d9;
}
.dialog-contents .detail-table-list ::v-deep .el-table {
  box-shadow: initial;
}
</style>
<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
import _ from 'lodash'
import common from '@/utils/common'
import * as define from '@/define'
import * as query from '@/queries/stock'
import message from '@/utils/message'

import BaseHeader from '@/components/molecules/BaseHeader'
import BaseSearchConditionModal from '@/components/organisms/BaseSearchConditionModal'
import BaseTableList from '@/components/molecules/BaseTableList'
import BasePagination from '@/components/molecules/BasePagination'

import StockMoveDialog from '@/components/pages/stock/StockMoveDialog'
import StockSetDialog from '@/components/pages/stock/StockSetDialog'

export default {
  name: 'StockBaseListPage',
  components: {
    BaseHeader,
    BaseSearchConditionModal,
    BaseTableList,
    BasePagination,
    StockMoveDialog,
    StockSetDialog
  },
  props: {
    name: {
      type: String,
      default: null
    },
    // 検索未対応コンポーネント用（暫定）
    noCondition: {
      type: Boolean
    },
    title: {
      type: String,
      default: ''
    },
    titleClass: {
      type: [String, Object, Array],
      default: ''
    },
    icon: {
      type: String,
      default: ''
    },
    tableClass: {
      type: [String, Object, Array],
      default: ''
    },
    baseQuery: {
      type: String,
      default: ''
    },
    storePath: {
      type: String,
      default: ''
    },
    responsePath: {
      type: String,
      default: ''
    },
    saveKey: {
      type: String,
      default: ''
    },
    tableSetList: {
      type: Array,
      default: () => { return [] }
    },
    tableDataList: {
      type: Array,
      default: () => { return [] }
    },
    showSearchComponent: {
      type: Boolean,
      default: true
    },
    showCustomSearchComponent: {
      type: Boolean,
      default: true
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    currentPage: {
      type: Number,
      default: undefined
    },
    currentPageKey: {
      type: String,
      default: 'currentPage'
    },
    sizeKey: {
      type: String,
      default: 'size'
    },
    saveCurrentPage: {
      type: Boolean,
      default: true
    },
    isParentLoaded: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      stockMoveKeys: {},
      stockSetKeys: {},
      localCurrentPage: 0
    }
  },
  computed: {
    define: () => define,
    ...mapState({
      sites: (state) => state.sites
    }),
    ...mapGetters('LocalStorage', {
      getSortCondition: 'sortCondition',
      getPaginationSize: 'paginationSize'
    }),
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    store () {
      return this.$store.state[this.storePath] || {}
    },
    keyword () {
      return this.store.searchKeyword || ''
    },
    isAndSearch () {
      return this.store.isAndSearch || false
    },
    conditions () {
      return this.store.searchConditions || {}
    },
    displayItems () {
      if (this.showPagination) {
        const head = this._currentPage * this.pageSize
        return this.tableDataList.slice(head, head + this.pageSize)
      } else {
        return this.tableDataList
      }
    },
    sortCondition () {
      return this.getSortCondition(this.name)
    },
    _currentPage () {
      if (this._saveCurrentPage) {
        return this.showPagination
          ? this.store[this.currentPageKey] || 0
          : 0
      } else {
        return this.currentPage != null ? this.currentPage : this.localCurrentPage || 0
      }
    },
    _saveCurrentPage () {
      return this.saveCurrentPage && this.currentPage == null
    },
    totalSize () {
      return this.tableDataList.length
    },
    pageSize () {
      return _.get(this.$store.state, `${this.storePath}.${this.sizeKey}`, 10)
    },
    totalCount () {
      return (this.$store.state[this.storePath] || {}).totalCount || 0
    }
  },
  watch: {
    isParentLoaded: {
      handler: function (val, oldVal) {
        // 親側でマスタ情報の取得完了後、保存されている検索条件を設定
        if (this.isParentLoaded) {
          common.recoverSearchConditions(this.saveKey, this.conditions, `${this.storePath}/searchKeyword`, `${this.storePath}/isAndSearch`, `${this.storePath}/maxGetCount`)
        }
      }
    }
  },
  created () {
    // ローカルストレージにサイズ情報がある場合は読み取りストアに保存。ない場合は10。
    this.setStore(`${this.storePath}/${this.sizeKey}`, this.getPaginationSize(this.name) || 10)

    if (this.showSearchComponent) {
      // 保存されている検索条件を設定。親側でマスタ情報の取得を先に行う必要がある場合、取得完了後に実施
      if (this.isParentLoaded) {
        common.recoverSearchConditions(this.saveKey, this.conditions, `${this.storePath}/searchKeyword`, `${this.storePath}/isAndSearch`, `${this.storePath}/maxGetCount`)
      }
    }
  },
  methods: {
    ...mapActions('LocalStorage', [
      'setSortCondition',
      'setPaginationSize'
    ]),
    ...mapMutations('EcStock', {
      updateListItem: 'update'
    }),
    setStore (key, val) {
      this.$store.dispatch({
        type: 'set',
        states: { [key]: val }
      })
    },
    onSearch (keyword, conditions) {
      this.$emit('search', keyword, conditions)
    },
    onSearched (result) {
      const key = _.get(Object.keys(_.get(result, 'data', {})), '0', '')
      const totalCount = _.get(result, `data.${key}.totalCount`, 0)
      const listSize = _.chain(result).get(`data.${key}.edges`).size().value()

      if (totalCount !== listSize && totalCount !== 0) {
        // 検索結果の件数が上限を超えた場合
        this.$notify.warning(message.MSG_305({ limit: listSize, count: totalCount }))
      }

      this.$emit('searched', result)
    },
    async onRefreshRow (keys) {
      try {
        const excQuery = common.addGraphQlCondition(query.GRAPH_QL_QUERY_EC_STOCK_LIST,
          { siteCodes: [keys.siteCode.toString()],
            stockTypes: [keys.stockType],
            withEmpty: [keys.withEmpty],
            productIds: keys.productId })
        const res = await common.postGraphQl(excQuery)
        // 結果が null の場合
        if (res.data.data.ecStockListItems.edges.length < 1) {
          this.$notify.error({
            message: '更新後データを取得できませんでした。'
          })
          return
        }
        this.ecStockDetail = res.data.data.ecStockListItems.edges[0]
        this.updateListItem(this.ecStockDetail)
      } catch (error) {
        throw error
      }
    },
    onRowClick (row, column, event) {
      this.$emit('row-click', row, column, event)
    },
    onSortChange ({ column, prop, order }) {
      this.setSortCondition({
        path: this.name,
        sort: { prop, order, sortBy: (!column || !column.sortBy) ? null : column.sortBy }
      })
      this.$emit('sort-change', { column, prop, order })
    },
    onPaginationCurrentChange (val) {
      this.localCurrentPage = val
      if (this._saveCurrentPage) {
        this.setStore(`${this.storePath}/${this.currentPageKey}`, val)
      }
      this.$emit('pagination-current-change', val)
    },
    onPaginationSizeChange (val) {
      this.setStore(`${this.storePath}/${this.sizeKey}`, val)
      this.setPaginationSize({ path: this.name, size: val })

      this.$emit('pagination-size-change', val)
    },
    rowAmountBtnLabel (row) {
      return row.node.ecStockDistr == null ? 0 : Number(row.node.ecStockDistr.amount)
    },
    rowRegisteredBtnLabel (row) {
      return row.node.site.type === 7
        ? '-'
        : row.node.ecStock == null
          ? 0
          : row.node.ecStock.registered == null
            ? '無制限'
            : Number(row.node.ecStock.registered)
    },
    /**
     * リンクのクリック（配分数）
     * @param {Object} 情報
     */
    onAmountButtonClick (row) {
      this.stockMoveKeys = {
        // 当該行のサイトコード
        siteCode: row.node.site.code,
        // 当該行の在庫種別
        stockType: row.node.stockType,
        // 当該行の商品ID(SKUコード)
        productId: row.node.sku.code
      }
    },
    /**
     * リンクのクリック（公開数）
     * @param {Object} 情報
     */
    onRegisteredButtonClick (row) {
      this.stockSetKeys = {
        // 当該行のサイトコード
        siteCode: row.node.site.code,
        // 当該行の在庫種別
        stockType: row.node.stockType,
        // 当該行の商品ID(SKUコード)
        productId: row.node.sku.code
      }
    },
    /**
     * リンクの表示（配分数）
     * @param {Object} 情報
     * @returns {name} 編集権限
     */
    amountReadonly (row) {
      // 在庫配分機能
      const distributionVal = _.get(
        this.getServiceSiteConfig(row.node.site.code, 'ec_stock', 'distribution', row.node.stockType), 'v.0', '')
      const hasDistr = distributionVal === '1'
      // 配分数のダイアログの表示権限
      const ableStockMove = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.STOCK_MOVE)
      // 配分数の設定権限
      const ableAmount = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.AMOUNT)

      return hasDistr && ableStockMove && ableAmount
    },
    /**
     * リンクの表示（公開数）
     * @param {Object} 情報
     * @returns {name} 編集権限
     */
    registeredReadonly (row) {
      // サイト種別が 7 (論理在庫) の場合「-」表示（リンクも表示しない)
      if (row.node.site.type === 7) {
        return false
      }
      // 在庫配分機能
      const distributionVal = _.get(
        this.getServiceSiteConfig(row.node.site.code, 'ec_stock', 'distribution', row.node.stockType), 'v.0', '')
      const hasDistr = distributionVal === '1'
      // 配分数の設定権限
      const ableAmount = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.AMOUNT)
      // 標準在庫数の設定権限
      const ableStandard = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.STANDARD)
      // 公開数の設定権限
      const ableRegistered = !this.$isFunctionDisabled(define.PermissionKeys.Stock.EcStock.List.REGSTERED)

      if ((!hasDistr && ableAmount) || ableStandard || ableRegistered) {
        return true
      } else {
        return false
      }
    }
  }
}
</script>
